import { money, pct } from './format.js';
import StatementUpload from './StatementUpload.jsx';
import FeeComparison from './FeeComparison.jsx';
import ExtractionReview from './ExtractionReview.jsx';
import UnpricedHoldings from './UnpricedHoldings.jsx';
import CoverageNote from './CoverageNote.jsx';

// Fees are abstract as a percentage and concrete as a dollar amount. The whole
// point of this section is the translation.
//
// It is also where the holdings the vendor could not price are surfaced. They
// cannot sit in the fee table itself (no expense ratio to put in the column),
// but they are the client's money and they belong on the same page as the
// positions they were separated from.

export default function FeeXraySection({ fees, bundle, unpriced, comparison, analysis, onStatus, onBundle, clientName, readOnly }) {
  if (!fees) {
    if (readOnly) return null;
    return (
      <section className="asmt-card asmt-fees" id="fees">
        <div className="asmt-card-head">
          <h2>What your investments cost you</h2>
        </div>
        <p className="asmt-lede">
          Send us the statements for these accounts and this becomes a
          position-by-position breakdown: what each fund charges, what that adds
          up to in dollars every year, and how concentrated the portfolio is.
        </p>
        <StatementUpload
          analysis={analysis}
          onStatus={onStatus}
          onBundle={onBundle}
          clientName={clientName}
        />
      </section>
    );
  }

  const { weightedExpenseRatioPct, annualFeeDollars, perHolding, flags, totalValue } = fees;
  const concentration = flags.singleStockConcentrationPct;

  return (
    <section className="asmt-card asmt-fees" id="fees">
      <div className="asmt-card-head">
        <h2>What your investments cost you</h2>
        <span className="asmt-card-note">
          {unpriced
            ? `${perHolding.length} priced positions · ${money(totalValue)} of ${money(unpriced.totalValue)}`
            : `${perHolding.length} positions · ${money(totalValue)}`}
        </span>
      </div>

      <div className="asmt-tiles" style={{ marginTop: 0 }}>
        <div className="asmt-tile">
          <div className="asmt-tile-label">Weighted expense ratio</div>
          <div className="asmt-tile-value">{pct(weightedExpenseRatioPct, 2)}</div>
          <div className="asmt-tile-note">across the funds you hold</div>
        </div>
        <div className="asmt-tile">
          <div className="asmt-tile-label">Cost every year</div>
          <div className="asmt-tile-value is-gap">{money(annualFeeDollars)}</div>
          <div className="asmt-tile-note">paid from the funds, never billed to you</div>
        </div>
        {concentration > 0 && (
          <div className="asmt-tile">
            <div className="asmt-tile-label">Largest single stock</div>
            <div className="asmt-tile-value">{pct(concentration, 1)}</div>
            <div className="asmt-tile-note">of the portfolio in one company</div>
          </div>
        )}
      </div>

      <CoverageNote unpriced={unpriced} figures="The cost and concentration figures above" />

      <table className="asmt-table">
        <thead>
          <tr>
            <th>Holding</th>
            <th className="num">Value</th>
            <th className="num">Weight</th>
            <th className="num">Expense ratio</th>
            <th className="num">Cost per year</th>
          </tr>
        </thead>
        <tbody>
          {perHolding.map((h, i) => (
            <tr key={`${h.ticker}-${i}`}>
              <td>
                <strong>{h.ticker || '-'}</strong>
                {h.name ? <span className="asmt-muted"> · {h.name}</span> : null}
              </td>
              <td className="num">{money(h.value)}</td>
              <td className="num">{pct(h.weightPct)}</td>
              <td className="num">
                {h.expenseRatioKnown ? pct(h.expenseRatioPct, 2) : <span className="asmt-muted">n/a</span>}
              </td>
              <td className="num">
                {h.expenseRatioKnown ? money(h.feeDollars) : <span className="asmt-muted">n/a</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {flags.unknownExpenseRatioCount > 0 && (
        <p className="asmt-card-note" style={{ marginTop: 10 }}>
          {flags.unknownExpenseRatioCount} position{flags.unknownExpenseRatioCount > 1 ? 's have' : ' has'} no
          published expense ratio and {flags.unknownExpenseRatioCount > 1 ? 'are' : 'is'} excluded from the
          average rather than counted as free.
        </p>
      )}

      <FeeComparison comparison={comparison} />

      <UnpricedHoldings unpriced={unpriced} />

      {concentration >= 5 && (
        <div className="asmt-callout">
          <strong>{pct(concentration, 1)} of the portfolio sits in a single company.</strong>{' '}
          That is usually employer stock, and it means the same company pays your
          income and holds a large share of your savings. Worth deciding
          deliberately rather than by default.
        </div>
      )}

      {/* A planner's checking tool, not part of the deliverable: it stays out
          of print and out of the client-facing view link. */}
      {!readOnly && <ExtractionReview bundle={bundle} />}

      {/* Reachable once a bundle exists, which the empty state above no longer
          covers. Two cases need it: a statement that read badly and should be
          sent again, and the sample household, whose whole point is to be a
          working report to try real statements against without a lead. */}
      {!readOnly && (
        <details className="asmt-restate">
          <summary>Analyze different statements</summary>
          <p className="asmt-restate-note">
            A new analysis replaces the positions, fees and pools above. Nothing
            from the questionnaire changes.
          </p>
          <StatementUpload
            analysis={analysis}
            onStatus={onStatus}
            onBundle={onBundle}
            clientName={clientName}
          />
        </details>
      )}
    </section>
  );
}
